import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { styles } from '@actual-app/components/styles';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import {
  addDays,
  daysBetween,
  planWeeks,
} from '@actual-app/core/shared/mtxbudget';
import type { Fortnight } from '@actual-app/core/shared/mtxbudget';

import { useNavigate } from '#hooks/useNavigate';

import { NotConfigured } from './QuinzainePage';
import { useWeekLabel, weekRange, WeekRow } from './semaines';
import {
  hoursLabel,
  longDate,
  MtxPage,
  Section,
  textDate,
  useMoney,
} from './ui';
import { useMtxbudget } from './useMtxbudget';

/** The pays shown before « Voir jusqu'à la paie du … ». */
const FIRST_PAYS = 4;

const weekday = new Intl.DateTimeFormat('fr-CA', {
  weekday: 'long',
  timeZone: 'UTC',
});

/** « 43,25 h » : hours worked, as entered. */
function doneHours(hours: number): string {
  return `${hours.toLocaleString('fr-CA', { maximumFractionDigits: 2 })} h`;
}

/**
 * The walk through the pays, week by week and told from today: « la semaine
 * prochaine », « dans 2 semaines ». Each figure is the one of Ma quinzaine.
 */
export function SemainesPage() {
  const { t } = useTranslation();
  const money = useMoney();
  const navigate = useNavigate();
  const weekLabel = useWeekLabel();
  const { config, plan, today, isLoading } = useMtxbudget();
  const [all, setAll] = useState(false);

  if (!config || plan.length === 0) {
    return (
      <MtxPage title={t('Semaine par semaine')} back="/quinzaine">
        {!isLoading && !config && <NotConfigured />}
      </MtxPage>
    );
  }

  const pay = config.pay;
  const weeks = planWeeks(plan, today, pay.minHoursPerWeek);
  const shown = all ? plan : plan.slice(0, FIRST_PAYS);
  const hoursOf = (fortnight: Fortnight): [number, number] =>
    config.pays[fortnight.payday]?.hours ?? [
      pay.minHoursPerWeek,
      pay.minHoursPerWeek,
    ];

  function payTitle(payday: string): string {
    const days = daysBetween(today, payday);
    if (days < 0) {
      return t('Paie du {{date}}', { date: textDate(payday) });
    }
    if (days === 0) {
      return t("Paie d'aujourd'hui");
    }
    if (days < 7) {
      return t('Paie de {{weekday}}, dans {{days}} jours', {
        weekday: weekday.format(new Date(`${payday}T00:00:00Z`)),
        days,
      });
    }
    return t('Paie dans {{days}} jours', { days });
  }

  function payDetail(fortnight: Fortnight): string {
    const missing = fortnight.charges.reduce(
      (sum, c) => sum + (c.coverage.kind === 'short' ? c.coverage.missing : 0),
      0,
    );
    const hours = hoursOf(fortnight);
    const amount =
      fortnight.status === 'worked' || fortnight.status === 'received'
        ? t('environ {{net}}', { net: money(fortnight.net) })
        : t('environ {{net}} à {{hours}}', {
            net: money(fortnight.net),
            hours: hoursLabel((hours[0] + hours[1]) / 2),
          });
    const earned =
      fortnight.status === 'worked' || fortnight.status === 'received';
    // What it covers is said of the pay already earned; of the others, only
    // what they lack.
    const cover =
      missing > 0
        ? t('il manque {{amount}}', { amount: money(missing) })
        : earned
          ? t("tout est couvert jusqu'au {{date}}", {
              date: textDate(addDays(fortnight.nextPayday, -1)),
            })
          : null;
    return [longDate(fortnight.payday), amount, cover]
      .filter(Boolean)
      .join(' · ');
  }

  return (
    <MtxPage title={t('Semaine par semaine')} back="/quinzaine">
      <View style={{ gap: 12, paddingBottom: 20 }}>
        <Text
          style={{
            fontSize: 13,
            color: theme.pageTextSubdued,
            padding: '0 4px',
          }}
        >
          {t(
            "Aujourd'hui, {{date}}. Le chiffre de chaque semaine est celui de Ma quinzaine.",
            { date: longDate(today) },
          )}
        </Text>

        {shown.map(fortnight => {
          const own = weeks.filter(w => w.payday === fortnight.payday);
          const isCurrent = own.some(w => w.offset === 0);
          return (
            <Section
              key={fortnight.payday}
              style={
                isCurrent
                  ? { border: `1px solid ${theme.buttonPrimaryBackground}` }
                  : undefined
              }
            >
              <Button
                variant="bare"
                aria-label={t('Voir le détail de la paie du {{date}}', {
                  date: textDate(fortnight.payday),
                })}
                onPress={() =>
                  navigate(`/quinzaine/parcours/paie?paie=${fortnight.payday}`)
                }
                style={{
                  justifyContent: 'flex-start',
                  gap: 8,
                  padding: '12px 16px',
                  borderRadius: 0,
                  borderBottom: `1px solid ${theme.tableBorder}`,
                  textAlign: 'left',
                }}
              >
                <View style={{ flex: 1 }}>
                  <Text style={{ fontWeight: 600, color: theme.pageTextDark }}>
                    {payTitle(fortnight.payday)}
                  </Text>
                  <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
                    {payDetail(fortnight)}
                  </Text>
                </View>
                <Text
                  aria-hidden="true"
                  style={{ fontSize: 18, color: theme.pageTextSubdued }}
                >
                  ›
                </Text>
              </Button>
              {own.map((week, index) =>
                week.offset < 0 ? (
                  <View
                    key={week.start}
                    style={{
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 16px',
                      borderBottom:
                        index === own.length - 1
                          ? undefined
                          : `1px solid ${theme.tableBorder}`,
                    }}
                  >
                    <View style={{ flex: 1 }}>
                      <Text
                        style={{ fontWeight: 500, color: theme.pageTextDark }}
                      >
                        {weekLabel(week.offset, today)}
                      </Text>
                      <Text
                        style={{ fontSize: 12, color: theme.pageTextSubdued }}
                      >
                        {weekRange(week.start, week.end)}
                      </Text>
                    </View>
                    <View style={{ alignItems: 'flex-end' }}>
                      <Text
                        style={{
                          fontSize: 18,
                          fontWeight: 600,
                          color: theme.pageTextDark,
                          ...styles.tnum,
                        }}
                      >
                        {doneHours(hoursOf(fortnight)[index === 0 ? 0 : 1])}
                      </Text>
                      <Text
                        style={{ fontSize: 12, color: theme.pageTextSubdued }}
                      >
                        {t('faites')}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <WeekRow
                    key={week.start}
                    week={week}
                    today={today}
                    last={index === own.length - 1}
                    strong={week.offset === 0}
                    withThreshold
                  />
                ),
              )}
            </Section>
          );
        })}

        {!all && plan.length > FIRST_PAYS && (
          <Button
            onPress={() => setAll(true)}
            style={{ minHeight: 44, fontSize: 13, fontWeight: 600 }}
          >
            {t("Voir jusqu'à la paie du {{date}}", {
              date: textDate(plan[plan.length - 1].payday),
            })}
          </Button>
        )}
      </View>
    </MtxPage>
  );
}
